import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export const enappgyMetaDataServiceApi = createApi({
  reducerPath: 'enappgyMetaDataServiceApi',
  baseQuery: fetchBaseQuery({
    baseUrl: "https://metadata-admin.dev.enappgy.io"
  }),
  tagTypes: [
    'Buildings',
    'User',
    'UserBuildings',
    'BuildingsMetadata',
    'SensorTypes',
    'Modules',
    'Users',
    'Tenants',
    'Profiles',
    'Alerts',
    'TenantsCatalog',
    'AlertsSettings',
    'UserMetadata',
    'ProfilesModules',
    'Reports',
    'UserTenants',
    'AlertsBuilding'
  ],

  endpoints: (builder) => ({
    //Building Queries
    getBuildingByTenant: builder.query({
      query: (tenantId) => `Buildings/GetByTenantIdWithFloorsAreas/${tenantId}`,
      providesTags: ['Buildings'],
    }),
    getBuildingByUserId: builder.query({
      query: (payload) =>
        `UserBuildings/GetByUserIdTenantIdWithProfiles?userId=${payload.userId}&tenantId=${payload.tenantId}`,
      providesTags: ['UserBuildings'],
    }),
    getBuildingMetadataByTenantId: builder.query({
      query: (tenantId) => `Buildings/GetByTenantId/${tenantId}`,
      providesTags: ['BuildingsMetadata'],
    }),
    editBuilding: builder.mutation({
      query: (payload) => ({
        url: `Buildings/${payload.id}/Update`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Buildings', 'BuildingsMetadata'],
    }),
    addBuildingModule: builder.mutation({
      query: (payload) => ({
        url: `BuildingModules/UpdateBuildingModules?buildingId=${payload[0].buildingId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        'Modules',
        'Buildings',
        'UserBuildings',
        'BuildingsMetadata',
      ],
    }),
    getBuildingModules: builder.query({
      query: (buildingId) => `BuildingModules/GetByBuildingId/${buildingId}`,
      providesTags: ['Modules'],
    }),
    deleteBuildingModule: builder.mutation({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join('&')
        return {
          url: `BuildingModules/Delete?${queryString}`,
          method: 'POST',
        }
      },
      invalidatesTags: [
        'Modules',
        'Buildings',
        'UserBuildings',
        'BuildingsMetadata',
      ],
    }),
    getBuildingSensorTypes: builder.query({
      query: (buildingId) =>
        `BuildingSensorTypes/GetByBuildingId/${buildingId}`,
      providesTags: ['SensorTypes'],
    }),
    addBuildingSensorType: builder.mutation({
      query: (payload) => ({
        url: `BuildingSensorTypes/UpdateBuildingSensorTypes?buildingId=${payload[0].buildingId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        'SensorTypes',
        'Buildings',
        'UserBuildings',
        'BuildingsMetadata',
      ],
    }),
    deleteBuildingSensorType: builder.mutation({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join('&')
        return {
          url: `BuildingSensorTypes/Delete?${queryString}`,
          method: 'POST',
        }
      },
      invalidatesTags: [
        'SensorTypes',
        'Buildings',
        'UserBuildings',
        'BuildingsMetadata',
      ],
    }),

    // Tenant Queries
    getTenants: builder.query({
      query: () => 'Tenants',
      providesTags: ['Tenants'],
    }),
    getTenantsWithBuildingProfiles: builder.query({
      query: () => 'Tenants/GetWithBuildingsProfiles',
      providesTags: ['TenantsCatalog'],
    }),

    // Users Queries
    getUserById: builder.query({
      query: (userId) => `Users/${userId}/GetUserComplete`,
      providesTags: ['User'],
    }),
    getAuth0Users: builder.query({
      query: (tenantId) => 'Users/GetAuth0UsersComplete',
      providesTags: ['Users'],
    }),
    getUserMetaDataById: builder.query({
      query: (userAuth0Id) =>
        `Users/GetAuth0UserCompleteByAuth0Id?auth0Id=${userAuth0Id}`,
      providesTags: ['UserMetadata'],
    }),
    getUserTenantsById: builder.query({
      query: (id) =>
      `UserTenants/GetByUserId/${id}`,
    providesTags: ['UserTenants'],
  }),
    createUser: builder.mutation({
      query: (payload) => ({
        url: 'Users',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Users', 'UserBuildings'],
    }),
    editUser: builder.mutation({
      query: (payload) => ({
        url: `Users/${payload.id}/Update`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User', 'UserMetadata', 'Users', 'UserBuildings'],
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `Users/${id}/Delete`,
        method: 'POST',
      }),
      invalidatesTags: ['User', 'UserMetadata', 'Users'],
    }),
    deleteUserBuilding: builder.mutation({
      query: (payload) => ({
        url: `UserBuildings/Delete?userId=${payload.userId}&buildingId=${payload.buildingId}`,
        method: 'POST',
      }),
      invalidatesTags: ['User', 'UserMetadata', 'Users', 'UserBuildings'],
    }),
    addUserBuilding: builder.mutation({
      query: (payload) => ({
        url: 'UserBuildings',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User', 'UserMetadata', 'Users', 'UserBuildings'],
    }),
    updateUserBuilding: builder.mutation({
      query: (payload) => ({
        url: `UserBuildings/Update?userId=${payload.userId}&buildingId=${payload.buildingId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User', 'UserMetadata', 'Users', 'UserBuildings'],
    }),
    addUserTenants: builder.mutation({
      query: (payload) => ({
        url: `UserTenants/UpdateUserTenants?userId=${payload[0].userId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User', 'UserMetadata', 'UserTenants','Users', 'UserBuildings'],
    }),

    //Profiles Queries
    getProfilesByBuildingId: builder.query({
      query: (buildingId) => `Profiles/GetByBuildingId/${buildingId}`,
      providesTags: ['Profiles'],
    }),
    getProfileModulesByProfileId: builder.query({
      query: (profileId) => `ProfileModules/GetByProfileId/${profileId}`,
      providesTags: ['ProfilesModules'],
    }),
    createProfile: builder.mutation({
      query: (payload) => ({
        url: 'Profiles',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Profiles', 'UserBuildings'],
    }),
    updateProfile: builder.mutation({
      query: (payload) => ({
        url: `Profiles/${payload.id}/Update`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Profiles', 'ProfilesModules', 'UserBuildings'],
    }),
    updateModulesForProfile: builder.mutation({
      query: (payload) => ({
        url: `ProfileModules/UpdateProfileModules?profileId=${payload[0].profileId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Profiles', 'ProfilesModules', 'UserBuildings'],
    }),
    deleteProfile: builder.mutation({
      query: (profileId) => ({
        url: `Profiles/${profileId}/Delete`,
        method: 'POST',
      }),
      invalidatesTags: ['Profiles', 'UserBuildings'],
    }),

    // Conditional Alerts
    getConditionalAlerts: builder.query({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join('&')
        return `ConditionalAlerts/GetByBuildingIdConditionalAlertStatus?${queryString}`
      },
      providesTags: ['Alerts'],
    }),
    getConditionalAlertsByBuilding: builder.query({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join('&')
        return `ConditionalAlerts/GetByBuildingId?${queryString}`
      },
      providesTags: ['AlertsBuilding'],
    }),
    getConditionalAlertsSettings: builder.query({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join('&')
        return `ConditionalAlertSettings/GetByBuildingId?${queryString}`
      },
      providesTags: ['AlertsSettings'],
    }),
    createConditionalAlertSettings: builder.mutation({
      query: (payload) => ({
        url: 'ConditionalAlertSettings',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['AlertsSettings'],
    }),
    updateConditionalAlertStatus: builder.mutation({
      query: (payload) => ({
        url: `ConditionalAlertSettings/${payload.id}/UpdateStatus?isEnabled=${payload.enabled}`,
        method: 'POST'
      }),
      invalidatesTags: ['AlertsSettings'],
    }),
    updateConditionalAlertSettings: builder.mutation({
      query: (payload) => ({
        url: `ConditionalAlertSettings/${payload.id}/UpdateNested`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['AlertsSettings'],
    }),
    deleteConditionalAlertSettings: builder.mutation({
      query: (id) => ({
        url: `ConditionalAlertSettings/${id}/Delete`,
        method: 'POST',
      }),
      invalidatesTags: ['AlertsSettings'],
    }),

    // ESG Reports
    getESGReports: builder.query({
      query: (filter) => {
        return `SdgReports/GetByBuildingId/${filter.buildingId}/${filter.year}`
      },
      providesTags: ['Reports'],
    }),

    //SensorTypes
    getSensorTypesCatalog: builder.query({
      query: (filter) => {
        return `SensorTypes`
      },
      providesTags: ['CatalogSensor'],
    })
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSensorTypesCatalogQuery,
  useUpdateConditionalAlertSettingsMutation,
  useUpdateConditionalAlertStatusMutation, 
  useGetConditionalAlertsByBuildingQuery,
  useGetUserTenantsByIdQuery,
  useDeleteConditionalAlertSettingsMutation,
  useCreateConditionalAlertSettingsMutation,
  useUpdateModulesForProfileMutation,
  useUpdateProfileMutation,
  useGetBuildingByUserIdQuery,
  useGetProfileModulesByProfileIdQuery,
  useGetConditionalAlertsSettingsQuery,
  useDeleteUserMutation,
  useUpdateUserBuildingMutation,
  useAddUserTenantsMutation,
  useDeleteUserBuildingMutation,
  useAddUserBuildingMutation,
  useGetTenantsWithBuildingProfilesQuery,
  useGetESGReportsQuery,
  useGetConditionalAlertsQuery,
  useGetBuildingSensorTypesQuery,
  useGetBuildingModulesQuery,
  useDeleteProfileMutation,
  useDeleteBuildingSensorTypeMutation,
  useGetBuildingMetadataByTenantIdQuery,
  useGetUserByIdQuery,
  useEditBuildingMutation,
  useGetTenantsQuery,
  useDeleteBuildingModuleMutation,
  useAddBuildingSensorTypeMutation,
  useAddBuildingModuleMutation,
  useCreateProfileMutation,
  useGetProfilesByBuildingIdQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useGetBuildingByTenantQuery,
  useGetAuth0UsersQuery,
  useGetUserMetaDataByIdQuery,
} = enappgyMetaDataServiceApi
